import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { consumeRedirectAfterAuth } from "../../utils/redirectAfterAuth";

// Shared post-social-login storage + onboarding routing, used by both the
// Google redirect callback and the Apple popup flow so the two never fork.

const API_URL = REST_API_URL;

export const looksLikeJwt = (t) =>
  typeof t === "string" &&
  t &&
  t !== "undefined" &&
  t !== "null" &&
  t.split(".").length === 3;

export const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

export const clearAuthStorage = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user_status");
  localStorage.removeItem("halper_remember_me");
  localStorage.removeItem("halper_saved_email");
  localStorage.removeItem("last_login_time");
  localStorage.removeItem("login_timezone");
  localStorage.removeItem("analyticsCurrency");
  localStorage.removeItem("analyticsCurrencyTimestamp");

  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user_status");
  sessionStorage.removeItem("last_login_time");
  sessionStorage.removeItem("login_timezone");
  sessionStorage.removeItem("onboarding_force_route");
  sessionStorage.removeItem("onboarding_force_status");
};

export const checkUserServices = async () => {
  try {
    const res = await fetch(`${API_URL}/service/my-services/`, {
      headers: buildHeaders(),
    });
    if (!res.ok) return false;
    const services = await res.json().catch(() => []);
    return Array.isArray(services) && services.length > 0;
  } catch {
    return false;
  }
};

export const checkOnboardingStatus = async () => {
  try {
    const res = await fetch(`${API_URL}/auth/onboarding-pass-check/`, {
      method: "GET",
      headers: buildHeaders(),
    });

    if (res.status === 200) return "COMPLETED";

    if (res.status === 400) {
      const err = await res.json().catch(() => ({}));
      const code = parseInt(
        Array.isArray(err.status) ? err.status[0] : err.status,
        10,
      );
      return Number.isFinite(code) ? code : null;
    }

    return null;
  } catch {
    return null;
  }
};

export const getRouteFromCode = (code, userStatus) => {
  if (userStatus === "individual") {
    switch (code) {
      case 222:
        return "/account-type-select";
      case 212:
        return "/biography-individual";
      case 214:
        return "/choose-niche";
      case 216:
        return "/my-services";
      case 218:
        return "/working-days";
      case 220:
        return "/booking-settings";
      case 240:
        return "/how-did-you-find";
      default:
        return null;
    }
  }

  if (userStatus === "salon") {
    switch (code) {
      case 222:
        return "/account-type-select";
      case 224:
        return "/biography-salon";
      case 226:
        return "/choose-niche";
      case 228:
        return "/my-services";
      case 230:
        return "/working-days";
      case 232:
        return "/booking-settings";
      case 238:
        return "/how-did-you-find";
      default:
        return null;
    }
  }

  if (userStatus === "worker") {
    switch (code) {
      case 222:
        return "/account-type-select";
      case 234:
        return "/worker-biography";
      case 236:
        return "/working-days";
      default:
        return null;
    }
  }

  return null;
};

/**
 * Store tokens from a successful social login and route the user to the
 * right place (dashboard if onboarding complete, else the correct onboarding
 * step). If `userStatus` is not supplied it is derived from /auth/userinfo/.
 */
export const finishSocialLogin = async ({
  access,
  refresh,
  userStatus = null,
  navigate,
  backTo = null,
  providerName = "",
}) => {
  if (!looksLikeJwt(access) || !looksLikeJwt(refresh)) {
    clearAuthStorage();
    toast.error(providerName ? `${providerName} login error` : "Login error");
    navigate("/sign-in", { replace: true });
    return;
  }

  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user_status");

  localStorage.setItem("accessToken", access);
  localStorage.setItem("refreshToken", refresh);

  const tz = getUserTimeZone();
  localStorage.setItem("login_timezone", tz);
  localStorage.setItem("last_login_time", new Date().toISOString());

  let status = userStatus;
  if (!status) {
    try {
      const res = await fetch(`${API_URL}/auth/userinfo/`, {
        headers: buildHeaders(),
      });

      if (res.status === 401) {
        clearAuthStorage();
        toast.error("Session expired. Please sign in again.");
        navigate("/sign-in", { replace: true });
        return;
      }

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        status =
          data?.user_status ||
          data?.status ||
          data?.user?.status ||
          data?.user?.user_status ||
          null;
      }
    } catch (e) {
      console.error("Failed to fetch user status after social login:", e);
    }
  }

  if (status) localStorage.setItem("user_status", String(status));

  if (providerName) toast.success(`Signed in with ${providerName}!`);

  const onboardingStatus = await checkOnboardingStatus();
  const hasServices = await checkUserServices();

  const safeBackTo =
    typeof backTo === "string" && backTo.startsWith("/") ? backTo : null;

  if (onboardingStatus === "COMPLETED" && hasServices) {
    navigate(safeBackTo || consumeRedirectAfterAuth() || "/profile", {
      replace: true,
    });
    return;
  }

  if (String(status) === "salon" && onboardingStatus === 222) {
    try {
      await fetch(`${API_URL}/auth/update-onboarding/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ onboarding_code: 224, timeZone: tz }),
      });
    } catch {
      /* non-fatal */
    }
    navigate("/biography-salon", { replace: true });
    return;
  }

  if (typeof onboardingStatus === "number") {
    const route = getRouteFromCode(onboardingStatus, String(status));
    if (route) {
      navigate(route, { replace: true });
      return;
    }
  }

  navigate("/account-type-select", { replace: true });
};
